import React from "react";
import { useAuth } from "contexts/api";
import type { UserWithRole } from "api/auth/Auth.typed";

export type PlanningEmployeesState = {
  pickerUsers: UserWithRole[];
  /** Full directory: user id → role.is_employee (for "Ma société" calendar scope). */
  userIsEmployeeById: Map<number, boolean>;
};

export function usePlanningEmployees(
  canAccessPlanning: boolean,
  canShowPlanningFilters: boolean,
  hasPlanningAllView: boolean,
): PlanningEmployeesState {
  const { getUsers, refreshUsersMap } = useAuth();
  const [pickerUsers, setPickerUsers] = React.useState<UserWithRole[]>([]);
  const [userIsEmployeeById, setUserIsEmployeeById] = React.useState<Map<number, boolean>>(
    () => new Map(),
  );

  const getUsersRef = React.useRef(getUsers);
  getUsersRef.current = getUsers;

  const refreshUsersMapRef = React.useRef(refreshUsersMap);
  refreshUsersMapRef.current = refreshUsersMap;

  React.useEffect(() => {
    if (!canAccessPlanning || !canShowPlanningFilters) {
      setPickerUsers([]);
      setUserIsEmployeeById(new Map());
      return;
    }

    let cancelled = false;

    void (async () => {
      const [list, salaried] = await Promise.all([
        getUsersRef.current(),
        getUsersRef.current(true),
      ]);
      if (cancelled) return;

      if (!Array.isArray(list)) {
        setPickerUsers([]);
        setUserIsEmployeeById(new Map());
        return;
      }

      await refreshUsersMapRef.current(list);
      if (cancelled) return;

      // Salaried flag comes from the API: the role is stripped without users.roles.view.
      const salariedIds = new Set((salaried ?? []).map((row) => row.user?.id));
      const roleMap = new Map<number, boolean>();
      for (const row of list) {
        const id = row.user?.id;
        if (id == null) continue;
        roleMap.set(id, salariedIds.has(id));
      }

      setUserIsEmployeeById(roleMap);
      setPickerUsers(hasPlanningAllView ? list : (salaried ?? []));
    })();

    return () => {
      cancelled = true;
    };
  }, [canAccessPlanning, canShowPlanningFilters, hasPlanningAllView]);

  return { pickerUsers, userIsEmployeeById };
}
